import { configureStore, combineReducers, Middleware } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import { Severity } from '@sentry/react';
import { isEnabled as isSentryEnabled } from '@sureapp/wl-toggle-common/dist/services/sentry';
import sessionPersistor from '@sureapp/wl-toggle-common/dist/utils/session_persistor';
import LogRocket from 'logrocket';
import qs from 'qs';
import logger from 'redux-logger';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import { isEnabled as isLogRocketEnabled } from '@@/auto/utils/logRocket';

import reducers from './reducers';

/**
 * Configure store
 */
const middlewares: Middleware[] = [];

if (isSentryEnabled()) {
  middlewares.push((_store) => (next) => (action) => {
    Sentry.addBreadcrumb({
      message: action.type,
      category: 'redux_action',
      level: Severity.Info,
      data: action.payload || action,
    });
    next(action);
  });
}
if (process.env.NODE_ENV !== 'test') {
  if (process.env.REACT_APP_ENV !== 'production') {
    middlewares.push(logger);
  }
  if (isLogRocketEnabled()) {
    middlewares.push(LogRocket.reduxMiddleware());
  }
}

const urlParams = qs.parse(window.location.search, { ignoreQueryPrefix: true });
const preloadedState = {
  ...sessionPersistor.getSessionState(),
  urlParams,
};

const persistedReducer = persistReducer(
  {
    key: 'toggleapp',
    storage,
    whitelist: [
      'oktaAccessToken',
      'oktaIdToken',
      'user',
      'enrollmentApplication',
      'quoteData',
      'stagedAutoPolicy',
      'policyHealthCheckErrorDetails',
    ],
  },
  combineReducers(reducers)
);

const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat(middlewares),
  devTools: process.env.REACT_APP_ENV !== 'production',
  preloadedState,
});

sessionPersistor.createPersistor(store);

export const persistor = persistStore(store);
export { store };
export type TAppDispatch = typeof store.dispatch;
export type TAppStoreState = ReturnType<typeof store.getState>;
